/* eslint-disable react/prop-types */
import { useToast } from "@egaranti/components";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@egaranti/components";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import BillUploadDialog from "./edit/BillUploadDialog";
import CustomerInfo from "./edit/CustomerInfo";
import DeleteProductDialog from "./edit/DeleteProductDialog";
import ProductList from "./edit/ProductList";
import WarrantyDateDialog from "./edit/WarrantyDateDialog";

import individualCustomersService from "@/services/individualCustomersService";

const CustomerDetailSheet = ({ open, onOpenChange, customerId }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [data, setData] = useState({});
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    size: 10,
  });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductToDelete, setSelectedProductToDelete] = useState(null);
  const [isWarrantyDateDialogOpen, setIsWarrantyDateDialogOpen] =
    useState(false);
  const [newWarrantyDate, setNewWarrantyDate] = useState(null);
  const [selectedProductForBill, setSelectedProductForBill] = useState(null);

  const fetchProducts = async (page) => {
    const res = await individualCustomersService.getIndividualCustomerProducts(
      customerId,
      {
        page: page,
        size: pagination.size,
      },
    );
    setProducts(res.content);
    setPagination((prev) => ({
      ...prev,
      page: res.page,
      totalPages: res.totalPages,
      totalElements: res.totalElements,
      size: res.size,
    }));
  };

  useEffect(() => {
    if (customerId && open) {
      individualCustomersService
        .getIndividualCustomer(customerId)
        .then((res) => {
          setData(res);
        });
      fetchProducts(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, open]);

  const onClickDelete = (productId) => {
    individualCustomersService
      .deleteIndividualCustomerProduct(customerId, productId)
      .then(() => {
        toast({
          title: t("common.productDeleted"),
          variant: "success",
        });
      })
      .then(() => fetchProducts(pagination.page));
  };

  const onPageChange = (page) => {
    setPagination((prev) => ({
      ...prev,
      page,
    }));
    fetchProducts(page);
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(
        dateString.replace(/(\d{2})-(\d{2})-(\d{4})/, "$3-$2-$1"),
      );
      if (isNaN(date.getTime())) return "";

      return new Intl.DateTimeFormat("tr-TR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    } catch (error) {
      return "";
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(
        dateString.replace(/(\d{2})-(\d{2})-(\d{4})/, "$3-$2-$1"),
      );
      if (isNaN(date.getTime())) return "";

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (error) {
      return "";
    }
  };

  const formatDateForApi = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `${day}-${month}-${year} ${hours}:${minutes}`;
    } catch (error) {
      return "";
    }
  };

  const handleWarrantyDateChange = async () => {
    try {
      if (!newWarrantyDate || !selectedProduct) return;

      const formattedDate = formatDateForApi(newWarrantyDate);
      if (!formattedDate) {
        toast({
          title: t("individualCustomers.invalidDate", "Geçersiz tarih formatı"),
          variant: "error",
        });
        return;
      }

      await individualCustomersService.updateWarrantyStartDate(
        selectedProduct.id,
        formattedDate,
      );

      toast({
        title: t("common.success"),
        description: t(
          "individualCustomers.warrantyDateUpdated",
          "Garanti başlangıç tarihi güncellendi",
        ),
        variant: "success",
      });

      setIsWarrantyDateDialogOpen(false);
      setNewWarrantyDate(null);

      await fetchProducts(pagination.page);
    } catch (error) {
      toast({
        title: error.response?.data?.errors?.[0]?.message || t("common.error"),
        variant: "error",
      });
    }
  };

  const handleBillUpload = async (file) => {
    try {
      await individualCustomersService.uploadBill(
        selectedProductForBill.id,
        file,
      );
      await fetchProducts(pagination.page);
      toast({
        title: t("common.success"),
        description: t("clientsPage.billUploaded"),
        variant: "success",
      });
    } catch (error) {
      toast({
        title: error.response?.data?.errors?.[0]?.message || t("common.error"),
        variant: "error",
      });
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full overflow-y-auto bg-white sm:max-w-3xl">
          <SheetHeader className="mb-6">
            <SheetTitle>
              {t("clientsPage.customerDetail", "Müşteri Detayı")}
            </SheetTitle>
          </SheetHeader>

          <div className="flex flex-col gap-6 pb-20">
            <CustomerInfo data={data} />
            <ProductList
              products={products}
              pagination={pagination}
              onPageChange={onPageChange}
              onDeleteProduct={setSelectedProductToDelete}
              onEditWarrantyDate={(product) => {
                setSelectedProduct(product);
                setIsWarrantyDateDialogOpen(true);
              }}
              onUploadBill={setSelectedProductForBill}
            />
          </div>
        </SheetContent>
      </Sheet>

      <WarrantyDateDialog
        open={isWarrantyDateDialogOpen}
        onOpenChange={(open) => {
          setIsWarrantyDateDialogOpen(open);
          if (!open) setNewWarrantyDate(null);
        }}
        selectedProduct={selectedProduct}
        newWarrantyDate={newWarrantyDate}
        onWarrantyDateChange={setNewWarrantyDate}
        onSave={handleWarrantyDateChange}
        formatDateForInput={formatDateForInput}
        formatDateForDisplay={formatDateForDisplay}
      />

      <DeleteProductDialog
        open={!!selectedProductToDelete}
        onOpenChange={() => setSelectedProductToDelete(null)}
        onConfirm={() => {
          if (selectedProductToDelete) {
            onClickDelete(selectedProductToDelete.id);
            setSelectedProductToDelete(null);
          }
        }}
      />

      <BillUploadDialog
        open={!!selectedProductForBill}
        onOpenChange={(open) => !open && setSelectedProductForBill(null)}
        onUpload={handleBillUpload}
      />
    </>
  );
};

export default CustomerDetailSheet;
